// ============================================================
// parseCsv.ts
// Hand-rolled RFC-4180 CSV parser (no library available). The INVERSE
// of exportCasesCsv.ts's escaping:
//   - fields may be quoted with " ; inside a quoted field, "" is a
//     literal quote, and commas / CR / LF are literal (not delimiters).
//   - rows are separated by CRLF or LF (both tolerated); a stray CR is
//     also treated as a row break.
//   - a leading UTF-8 BOM is stripped.
//   - a trailing newline does not produce a spurious empty row.
//
// Returns { headers, rows } where headers is the first line's cells and
// rows is every subsequent line as a string[] aligned to headers by
// index. Ragged rows (fewer/more cells than headers) are returned as-is;
// the caller pads/truncates when reading by column index.
// ============================================================

export interface ParsedCsv {
  headers: string[]
  rows: string[][]
  // True when the text ended INSIDE a quoted field — an unbalanced quote
  // swallowed everything after it into one cell. The importer refuses the
  // file rather than silently importing a truncated/garbled row set.
  unterminatedQuote?: boolean
}

// Tokenise the whole text into rows of cells in a single pass, tracking
// whether we're inside a quoted field. This is the only correct way to
// respect commas/newlines embedded in quotes.
const parseRows = (text: string): { rows: string[][]; unterminatedQuote: boolean } => {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false
  let i = 0
  const n = text.length

  const endCell = (): void => { row.push(cell); cell = '' }
  const endRow = (): void => { endCell(); rows.push(row); row = [] }

  while (i < n) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        // Escaped quote ("") → literal ", else close the quoted field.
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue }
        inQuotes = false; i++; continue
      }
      cell += ch; i++; continue
    }
    // Not in quotes.
    if (ch === '"') { inQuotes = true; i++; continue }
    if (ch === ',') { endCell(); i++; continue }
    if (ch === '\r') {
      // Treat \r or \r\n as one row break.
      endRow()
      if (text[i + 1] === '\n') i += 2; else i++
      continue
    }
    if (ch === '\n') { endRow(); i++; continue }
    cell += ch; i++
  }
  // Flush the final cell/row unless the text ended exactly on a row break
  // (in which case row is empty and cell is '' → don't emit a blank row).
  if (cell !== '' || row.length > 0) endRow()
  return { rows, unterminatedQuote: inQuotes }
}

// Parse CSV text into headers + data rows. Strips a BOM, drops fully
// blank trailing rows, and returns empty headers/rows for empty input.
export const parseCsv = (raw: string): ParsedCsv => {
  if (!raw) return { headers: [], rows: [] }
  // Strip a leading UTF-8 BOM if present.
  const text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw
  const { rows: all, unterminatedQuote } = parseRows(text)
  // Drop any fully-empty rows — a blank line (single '' cell) OR a row of
  // only delimiters/whitespace (",,,", Excel's padding) — so they don't
  // import as "missing name" failures.
  const nonEmpty = all.filter((r) => r.some((c) => c.trim() !== ''))
  if (nonEmpty.length === 0) return { headers: [], rows: [], unterminatedQuote }
  const [headers, ...rows] = nonEmpty
  return { headers, rows, unterminatedQuote }
}
